import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Feature,
  FeatureNames,
  OperatingProgramProperties,
  ProgramCommands,
  ScheduleCommands,
  ScheduleProperties,
} from '@vi-iot/client';

import { FeatureService, InstallationFeaturesStore, isReady } from '@store';

import { ScheduleEntry, ScheduleService } from '../../../../../../../features/timemanager/services';
import { HeatingCircuitIgnoredModalService } from '../../../../../heating-circuit-ignored-modal/heating-circuit-ignored-modal.service';
import { HeatingPanelService, HEATING_PROGRAMS } from '../heating-panel.service';

export enum MultiFamilyScheduleType {
  none,
  heating,
  dhw,
  circulation,
}

@Component({
  selector: 'app-multi-family-house-panel',
  templateUrl: './multi-family-house-panel.component.html',
  styleUrls: ['./multi-family-house-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiFamilyHousePanelComponent implements OnInit {
  @Input() circuitId: string;
  HEATING_PROGRAMS = HEATING_PROGRAMS;

  activeTab: string;
  activeTabIndex = 0;
  editMultipleDays: boolean;
  modes: Observable<Feature<any, unknown>>[] = [];

  ngUnsubscribe: Subject<void> = new Subject<void>();

  tabs$: Observable<
    [
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>
    ]
  >;
  heatingSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  dhwSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  circulationSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  temperaturePanelEcoFeature$: Observable<Feature<OperatingProgramProperties, ProgramCommands>>;
  temperaturePanelComfortFeature$: Observable<Feature<OperatingProgramProperties, ProgramCommands>>;

  constructor(
    public heatingPanelService: HeatingPanelService,
    private featureService: FeatureService,
    private scheduleService: ScheduleService,
    private heatingCircuitIgnoredModalService: HeatingCircuitIgnoredModalService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.heatingSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingSchedule(this.circuitId)
    );
    this.dhwSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitDhwSchedule(this.circuitId)
    );
    this.circulationSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitCirculationSchedule(this.circuitId)
    );

    this.tabs$ = combineLatest([
      this.heatingSchedule$,
      this.dhwSchedule$,
      this.circulationSchedule$,
    ]);

    this.modes = this.heatingPanelService.modes;

    this.temperaturePanelEcoFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitOperatingProgram(this.circuitId, HEATING_PROGRAMS.ECO)
    );
    this.temperaturePanelComfortFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitOperatingProgram(this.circuitId, HEATING_PROGRAMS.COMFORT)
    );
  }

  onTabChange(feature: Feature<ScheduleProperties, ScheduleCommands>, index: number): void {
    const scheduleFeature = cloneDeep(feature);
    this.activeTab = scheduleFeature.feature;
    this.scheduleService.createSchedule(scheduleFeature);
    this.scheduleService.createModes(this.featureService.getScheduleModes(scheduleFeature));
    this.activeTabIndex = index;
  }

  initTabs(
    features: [
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>
    ]
  ): boolean {
    if (!this.activeTab) {
      const tempTabIndex = this.getActiveTabIndex(features);
      if (Number.isInteger(tempTabIndex)) {
        this.activeTabIndex = tempTabIndex;
        this.onTabChange(features[this.activeTabIndex], this.activeTabIndex);
      } else {
        this.editMultipleDays = false;

        return false;
      }
    } else {
      const feature = features.find((f, index) => {
        const found = f.feature === this.activeTab;

        if (found) {
          this.activeTabIndex = index;
        }

        return found;
      });
      this.onTabChange(feature, this.activeTabIndex);
      this.editMultipleDays = true;

      return true;
    }
  }

  onMultipleFamilyScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Array<Feature<ScheduleProperties, ScheduleCommands>>
  ): void {
    this.heatingCircuitIgnoredModalService
      .showModalWhenSrcIsActive(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const scheduleFeature = cloneDeep(feature);
        const schedule = scheduleFeature[this.activeTabIndex]?.properties?.entries?.value || {
          mon: [],
          tue: [],
          wed: [],
          thu: [],
          fri: [],
          sat: [],
          sun: [],
        };
        const value = this.scheduleService.getScheduleValue(
          event.scheduleIds,
          event.periods,
          schedule
        );
        const tabType = this.getTabType(this.activeTab);
        let featureName: string;
        switch (tabType) {
          case MultiFamilyScheduleType.heating:
            featureName = FeatureNames.circuitHeatingSchedule(this.circuitId);
            break;
          case MultiFamilyScheduleType.dhw:
            featureName = FeatureNames.circuitDhwSchedule(this.circuitId);
            break;
          case MultiFamilyScheduleType.circulation:
            featureName = FeatureNames.circuitCirculationSchedule(this.circuitId);
            break;
          default:
            break;
        }

        if (featureName) {
          this.installationFeaturesStore.updateFeature(
            featureName,
            cloneDeep(value),
            'entries',
            'newSchedule',
            'setSchedule'
          );
        }
      });
  }

  private getTabType(activeTab: string): MultiFamilyScheduleType {
    if (activeTab.indexOf('heating.schedule') >= 0) {
      return MultiFamilyScheduleType.heating;
    } else if (activeTab.indexOf('dhw.schedule') >= 0) {
      return MultiFamilyScheduleType.dhw;
    } else if (activeTab.indexOf('circulation.schedule') >= 0) {
      return MultiFamilyScheduleType.circulation;
    } else {
      return MultiFamilyScheduleType.none;
    }
  }

  private getActiveTabIndex(
    features: [
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>,
      Feature<ScheduleProperties, ScheduleCommands>
    ]
  ): number {
    let activeTabIndex = null;
    if (features) {
      if (isReady(features[0]) && !isEmpty(features[0].commands)) {
        activeTabIndex = 0;
      } else if (isReady(features[1]) && !isEmpty(features[1].commands)) {
        activeTabIndex = 1;
      }
    }

    return activeTabIndex;
  }
}
